import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";
import { set } from "date-fns";

// Create the EmploymentTypeMasterContext
const WorkplaceMasterContext = createContext();

// Custom hook to use the EmploymentTypeMasterContext
export const useWorkplaceMaster = () => useContext(WorkplaceMasterContext);

// Provider component to wrap the app
export const WorkplaceMasterProvider = ({ children }) => {
  const [workplaces, setWorkplace] = useState([]);
  const API_URL = "https://hrmspayroll.prepstripe.com/api/masters/workplaces"; // Replace with your actual API URL
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);

  const fetchWorkplace = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const { data: workplaceArray, current_page, last_page, per_page, total } = response.data.data;

      setWorkplace(workplaceArray || []);
      setPagination({
        currentPage: current_page || 1,
        totalPages: last_page || 1,
        totalItems: total || 0,
        perPage: per_page || 10,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching workplace master:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch workplace master from the API on load
  useEffect(() => {
    fetchWorkplace();
  }, [API_URL, token]);

  // Add a new workplace
  const addWorkplace = async (newWorkplace) => {
    try {
      const response = await axios.post(API_URL, newWorkplace, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setWorkplace((prev) => [response.data?.data, ...prev]);
    } catch (error) {
      console.error("Error adding workplace:", error);
    }
  };

  // Update an existing workplace
  const updateWorkplace = async (updatedWorkplace) => {
    try {
      const response = await axios.put(
        `${API_URL}/${updatedWorkplace.id}`,
        updatedWorkplace,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );
      setWorkplace((prev) =>
        prev.map((workplace) =>
          workplace.id === updatedWorkplace.id
            ? { ...workplace, ...response.data?.data }
            : workplace,
        ),
      );
    } catch (error) {
      console.error("Error updating workplace master:", error);
    }
  };

  // Delete a employment-type
  const deleteWorkplace = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setWorkplace((prev) => prev.filter((workplace) => workplace.id !== id));
    } catch (error) {
      console.error("Error deleting workplace:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      workplaces,
      addWorkplace,
      updateWorkplace,
      deleteWorkplace,
      pagination,
      fetchWorkplace,
    }),
    [workplaces, isLoading, pagination], // Recalculate the value only when `workplace` changes
  );

  return (
    <WorkplaceMasterContext.Provider value={value}>
      {children}
    </WorkplaceMasterContext.Provider>
  );
};
